import axiosInstance from '@/utils/appsettings';
import { gtmPushFreeSubscription } from '@/utils/gtm';
import { emitCreditUpdate } from '@/utils/creditEvents';

/**
 * Releases a checkout subscription whose card step failed, so it doesn't stay alive in Stripe
 * (retried, and able to bill beside the user's next attempt). Fire-and-forget: the server only
 * cancels the caller's own never-paid subscription, and its daily sweep catches anything this misses.
 */
export const abandonUnpaidSubscription = (subscriptionId: string): void => {
  axiosInstance
    .post('/billing/payment-intent/abandon', { subscription_id: subscriptionId })
    .catch(() => {
      // Best effort — never surface a cleanup failure over the payment error the user is already seeing.
    });
};

/**
 * Subscribes a workspace to a $0 plan (no payment method). Shared by the payment page's
 * "Activate free plan" button and the new-account auto-activation in BillingFlowGuard.
 * Free plans skip /billing/success, so its analytics + shell credit refresh fire here. Throws on failure.
 */
export const activateFreePlan = async (params: {
  token: string;
  plan: { id: string; name: string; display_name: string };
  workspaceId: string | number;
  billingCycle?: string;
  voucherCode?: string;
}): Promise<void> => {
  const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/billing/subscription`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${params.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      plan_id: params.plan.id,
      billing_cycle: params.billingCycle ?? 'monthly',
      payment_method_id: null,
      trial_days: 0,
      workspace_id: params.workspaceId,
      voucher_code: params.voucherCode,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Subscription creation failed');
  }

  await response.json();

  gtmPushFreeSubscription({
    planId: params.plan.name,
    planName: params.plan.display_name,
  });
  emitCreditUpdate('billing-success');
};

export const sumAllocationMetric = (
  allocations: any[],
  typeMatches: string[],
  metric: 'allocated' | 'consumed' | 'remaining'
) => {
  if (!allocations || !Array.isArray(allocations)) return 0;
  return allocations
    .filter(a => typeMatches.some(t => a.tier_type?.toLowerCase().includes(t.toLowerCase())))
    .reduce((sum, current) => sum + (current[metric] || 0), 0);
};

export const calculateCreditSums = (allocations: any[], planName: string, usageSummary: any) => {
  const safeAllocations = allocations || [];

  // A daily allocation row's own allocated/remaining only describe that one row — they
  // don't know whether the plan's overall monthly cap for the tier has already been used
  // up on a different day. Prefer the backend's monthly-cap-aware today_allocated/
  // today_remaining when present; only sum raw rows as a fallback (e.g. while usageSummary
  // hasn't loaded yet).
  const hasTodayFigures = typeof usageSummary?.today_allocated === 'number'
    && typeof usageSummary?.today_remaining === 'number';

  const freeAllocated = hasTodayFigures
    ? usageSummary.today_allocated
    : sumAllocationMetric(safeAllocations, ['daily'], 'allocated');
  const freeConsumed = hasTodayFigures
    ? (usageSummary.today_consumed || 0)
    : sumAllocationMetric(safeAllocations, ['daily'], 'consumed');
  const freeRemaining = hasTodayFigures
    ? usageSummary.today_remaining
    : sumAllocationMetric(safeAllocations, ['daily'], 'remaining');

  const monthlyAllocated = sumAllocationMetric(safeAllocations, ['monthly'], 'allocated');
  const monthlyConsumed = sumAllocationMetric(safeAllocations, ['monthly'], 'consumed');
  const monthlyRemaining = sumAllocationMetric(safeAllocations, ['monthly'], 'remaining');

  const topupAllocated = sumAllocationMetric(safeAllocations, ['bundle'], 'allocated');
  const topupConsumed = sumAllocationMetric(safeAllocations, ['bundle'], 'consumed');
  const topupRemaining = sumAllocationMetric(safeAllocations, ['bundle'], 'remaining');

  // Superseded by the three explicit bonus categories below — kept only so this
  // shared function's return shape doesn't change for any other existing caller.
  const bonusAllocated = sumAllocationMetric(safeAllocations, ['bonus'], 'allocated');
  const bonusConsumed = sumAllocationMetric(safeAllocations, ['bonus'], 'consumed');
  const bonusRemaining = sumAllocationMetric(safeAllocations, ['bonus'], 'remaining');

  const referralAllocated = sumAllocationMetric(safeAllocations, ['referral'], 'allocated');
  const referralConsumed = sumAllocationMetric(safeAllocations, ['referral'], 'consumed');
  const referralRemaining = sumAllocationMetric(safeAllocations, ['referral'], 'remaining');

  // 'referrer'/'referee'/'influencer' are mutually exclusive substrings of the exact
  // tier_type strings (invite_referrer_bonus / invite_referee_bonus / influencer_bonus)
  // and never collide with the monthly/bundle/daily matches above.
  const referrerBonusAllocated = sumAllocationMetric(safeAllocations, ['referrer'], 'allocated');
  const referrerBonusConsumed = sumAllocationMetric(safeAllocations, ['referrer'], 'consumed');
  const referrerBonusRemaining = sumAllocationMetric(safeAllocations, ['referrer'], 'remaining');

  const refereeBonusAllocated = sumAllocationMetric(safeAllocations, ['referee'], 'allocated');
  const refereeBonusConsumed = sumAllocationMetric(safeAllocations, ['referee'], 'consumed');
  const refereeBonusRemaining = sumAllocationMetric(safeAllocations, ['referee'], 'remaining');

  const influencerBonusAllocated = sumAllocationMetric(safeAllocations, ['influencer'], 'allocated');
  const influencerBonusConsumed = sumAllocationMetric(safeAllocations, ['influencer'], 'consumed');
  const influencerBonusRemaining = sumAllocationMetric(safeAllocations, ['influencer'], 'remaining');

  // Admin-granted tier_type is exactly 'bonus_credits', which none of the other tier strings contain.
  const adminBonusAllocated = sumAllocationMetric(safeAllocations, ['bonus_credits'], 'allocated');
  const adminBonusConsumed = sumAllocationMetric(safeAllocations, ['bonus_credits'], 'consumed');
  const adminBonusRemaining = sumAllocationMetric(safeAllocations, ['bonus_credits'], 'remaining');

  const hasTotalFigures = typeof usageSummary?.display_total_available === 'number';

  // "Total Available" is the account's fixed total for the month, not a snapshot of
  // what's currently allocatable — that distinction matters specifically for the daily
  // tier. today_allocated/total_allocated correctly drop toward 0 as the month's daily
  // credits get used up (that's the numerator's job), but the *denominator* shouldn't
  // shrink with it: daily_limit is the plan's fixed monthly cap for the daily tier and
  // stays constant regardless of consumption, so it's what the total is really "out of."
  const dailyTierMonthlyCap = typeof usageSummary?.daily_limit === 'number'
    ? usageSummary.daily_limit
    : freeAllocated;

  let rawTotalAllocated = hasTotalFigures
    ? dailyTierMonthlyCap + monthlyAllocated + topupAllocated
      + referrerBonusAllocated + refereeBonusAllocated + influencerBonusAllocated + adminBonusAllocated
    : safeAllocations.reduce((sum, curr) => sum + (curr.allocated || 0), 0);
  const rawTotalConsumed = hasTotalFigures
    ? (usageSummary.total_consumed || 0)
    : safeAllocations.reduce((sum, curr) => sum + (curr.consumed || 0), 0);
  let rawTotalRemaining = hasTotalFigures
    ? usageSummary.display_total_available
    : safeAllocations.reduce((sum, curr) => sum + (curr.remaining || 0), 0);

  // Free Plan Override Logic — only applies to the raw-sum fallback path above. The
  // backend totals are already monthly-cap-aware, so applying this on top of them would
  // reintroduce the fake-positive-balance bug this whole fix is for.
  const isFreePlan = planName?.toLowerCase().includes('free');
  const dailyLimit = usageSummary?.daily_limit;

  if (!hasTotalFigures && isFreePlan && typeof dailyLimit === 'number' && dailyLimit > rawTotalAllocated) {
    rawTotalAllocated = dailyLimit;
    rawTotalRemaining = Math.max(0, dailyLimit - rawTotalConsumed);
  }

  // "Resets at midnight" is only true when the plan's per-day grant (daily_credit_amount)
  // is smaller than its monthly cap for the tier (daily_limit) — otherwise the whole
  // month's daily-tier credits are already usable in a single day and nothing new arrives tomorrow.
  const dailyCreditAmount = usageSummary?.daily_credit_amount;
  const hasRecurringDailyReset = Boolean(
    typeof dailyCreditAmount === 'number' && dailyCreditAmount > 0
    && typeof dailyLimit === 'number' && dailyLimit > 0
    && dailyCreditAmount < dailyLimit
  );

  return {
    freeAllocated,
    freeConsumed,
    freeRemaining,
    monthlyAllocated,
    monthlyConsumed,
    monthlyRemaining,
    topupAllocated,
    topupConsumed,
    topupRemaining,
    bonusAllocated,
    bonusConsumed,
    bonusRemaining,
    referralAllocated,
    referralConsumed,
    referralRemaining,
    referrerBonusAllocated,
    referrerBonusConsumed,
    referrerBonusRemaining,
    refereeBonusAllocated,
    refereeBonusConsumed,
    refereeBonusRemaining,
    influencerBonusAllocated,
    influencerBonusConsumed,
    influencerBonusRemaining,
    adminBonusAllocated,
    adminBonusConsumed,
    adminBonusRemaining,
    totalAllocated: rawTotalAllocated,
    totalConsumed: rawTotalConsumed,
    totalRemaining: rawTotalRemaining,
    hasRecurringDailyReset
  };
};
